import type { StationsTextEntry } from "./types";
import type { GlossaryTerm, ValidationResult } from "./types";

const HTML_PATTERN = /<\/?(script|iframe|object|embed)/i;
const PLACEHOLDER_PATTERN = /\{[^}]+\}/g;
const MAX_TEXT_LENGTH = 280;

function extractPlaceholders(text: string): string[] {
	return text.match(PLACEHOLDER_PATTERN) || [];
}

export function validateEntry(entry: StationsTextEntry, glossary: GlossaryTerm[]): ValidationResult[] {
	const results: ValidationResult[] = [];
	const { currentText, defaultText } = entry;

	// Rule: empty currentText
	if (currentText.trim() === "") {
		results.push({
			rule: "empty",
			severity: "warn",
			message: "Text is empty. This may hide UI labels for users.",
		});
	}

	// Rule: placeholder added/removed vs defaultText
	const defaultPlaceholders = extractPlaceholders(defaultText).sort();
	const currentPlaceholders = extractPlaceholders(currentText).sort();
	const missing = defaultPlaceholders.filter((p) => !currentPlaceholders.includes(p));
	const added = currentPlaceholders.filter((p) => !defaultPlaceholders.includes(p));
	if (missing.length > 0) {
		results.push({
			rule: "placeholder-removed",
			severity: "error",
			message: `Placeholder(s) removed from original: ${missing.join(", ")}`,
		});
	}
	if (added.length > 0) {
		results.push({
			rule: "placeholder-added",
			severity: "error",
			message: `Placeholder(s) added that were not in original: ${added.join(", ")}`,
		});
	}

	// Rule: length over max
	if (currentText.length > MAX_TEXT_LENGTH) {
		results.push({
			rule: "length",
			severity: "warn",
			message: `Text exceeds ${MAX_TEXT_LENGTH} characters (currently ${currentText.length}).`,
		});
	}

	// Rule: HTML/script tag
	if (HTML_PATTERN.test(currentText)) {
		results.push({
			rule: "html-tag",
			severity: "error",
			message: "Text contains unsafe HTML tags (script, iframe, object, embed).",
		});
	}

	// Rule: glossary mismatch
	for (const term of glossary) {
		const lowerText = currentText.toLowerCase();
		const lowerTerm = term.term.toLowerCase();
		if (lowerText.includes(lowerTerm) && !currentText.includes(term.canonical)) {
			results.push({
				rule: "glossary",
				severity: term.severity,
				message: `Use "${term.canonical}" instead of a case-variant (glossary rule).`,
			});
		}
	}

	return results;
}

export function hasErrors(results: ValidationResult[]): boolean {
	return results.some((r) => r.severity === "error");
}

export function hasWarnings(results: ValidationResult[]): boolean {
	return results.some((r) => r.severity === "warn");
}

export function validateTag(tag: string): string | null {
	if (!tag) return "Tag cannot be empty.";
	if (!/^[a-z0-9-]+$/.test(tag)) return "Tags must be lowercase kebab-case (a-z, 0-9, hyphens only).";
	if (tag.length > 40) return "Tag must be 40 characters or fewer.";
	return null;
}
